import color from "@definitions/mui/color";
import { fontFamSecondary } from "@definitions/mui/custom";
import { makeStyles } from "@mui/styles";

interface IProps {
  active?: boolean;
  isEdit?: boolean;
  isLogout?: boolean;
  noFullscreen?: boolean;
  isBtnOutline?: boolean;
  width?: number;
  height?: number;
  backgroundColor?: string;
  top?: number;
}

const useStyles = ({ active, isEdit, isLogout, noFullscreen, isBtnOutline }: IProps) => {
  return makeStyles(() => ({
    "@keyframes smoothScroll": {
      "0%": {
        transform: "translateY(-142px)",
      },
      "100%": {
        transform: "translateY(0)",
      },
    },
    "@keyframes smoothScrollBottom": {
      "0%": {
        transform: "translateY(100%)",
      },
      "100%": {
        transform: "translateY(0)",
      },
    },
    stickyHeader: {
      backgroundColor: color.white,
      position: "fixed",
      top: 0,
      width: "100%",
      boxShadow: "0px 6px 12px rgba(46, 67, 77, 0.1)",
      transition: "all 0.5s ease",
      animation: `$smoothScroll 1s forwards`,
      zIndex: 1,

      "& .MuiInputBase-root": {
        width: "100%",
      },
    },
    linkMenu: {
      position: "relative",
      "& svg": {
        width: 28,
        height: 28,
      },
      "&:after": {
        content: active ? "''" : "unset",
        width: 20,
        height: 2,
        backgroundColor: color.primary.main,
        borderRadius: 8,
        position: "absolute",
        bottom: 3,
        left: "50%",
        transform: "translateX(-50%)",
      },
    },
    clickLink: {
      "& span": {
        color: color.primary.main,
      },
    },
    menuList: {
      "& + div": {
        borderTop: `1px solid ${color.grey.light}`,
      },
    },
    groupDataList: {
      "& > div": {
        "& + div": {
          marginTop: isEdit ? 24 : 20,
          paddingTop: isEdit ? 24 : 20,
          borderTop: `1px solid ${color.grey.light}`,
        },
      },
      "& .MuiFormHelperText-root.Mui-error": {
        margin: 0,
        marginTop: 4,
        fontSize: 12,
        color: color.fault,
      },
      "& .Mui-error .MuiOutlinedInput-notchedOutline": {
        borderColor: color.fault,
      },
    },
    buttonStyle: {
      padding: 12,
      fontSize: 16,
      fontWeight: 700,
      width: "100%",
      backgroundColor: "transparent",
      color: color.primary.main,
      border: `1px solid ${color.primary.main}`,
      boxShadow: "none",
      lineHeight: "24px",
      fontFamily: fontFamSecondary,

      ...(isBtnOutline && {
        "&:hover": {
          backgroundColor: color.primary.main,
          color: color.white,
        },
      }),
    },
    inputBeforeStyle: { paddingLeft: 16 },
    menuGroup: {
      "& > div": {
        "& + div": {
          marginTop: 16,
        },
      },
    },
    dialogRoot: {
      margin: 0,
    },
    dialogContainer: {
      alignItems: "flex-end",
    },
    dialogLogout: {
      "& .MuiDialog-container": {
        alignItems: "flex-end",
      },
      "& .MuiPaper-root": {
        margin: 0,
        width: "100%",
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
      },
      "& .MuiDialog-paperScrollBody": {
        margin: 0,
        width: "100%",
        maxWidth: "100%",
        borderBottomLeftRadius: 0,
        borderBottomRightRadius: 0,
        verticalAlign: "bottom",
      },
      "& .MuiDialogTitle-root": {
        padding: 0,
        margin: 0,
        "& svg": {
          top: 27,
          right: 24,
        },
      },
      "& .MuiDialogContent-root": {
        margin: 0,
        padding: 24,
        // new style
        paddingTop: 46,
        paddingBottom: 16,
        position: "relative",
        "& .MuiDialogTitle-root": {
          padding: 0,
        },
        "& .MuiDialogContent-root": {
          padding: 0,
        },
      },
      "& .MuiDialogActions-root": {
        // marginTop: 24,
        marginTop: 16,
        padding: 0,
      },
      "&. MuiDialogActions-root": {
        "&:not(:first-of-type)": {
          marginLeft: 16,
        },
      },
      closeIcon: {
        top: 16,
        left: 16,
      },
    },
    radioList: {
      "& .MuiRadio-root": {
        padding: "0 8px",
        marginLeft: "4px",
        "& input": {
          "& + span": {
            width: 20,
            height: 20,
            border: `2px solid ${color.inactive.secondary}`,
            borderRadius: "50%",
            "& svg": {
              display: "none",
            },
            "&:after": {
              transform: "scale(0)",
            },
          },
        },
        "&.Mui-checked": {
          "& input": {
            "& + span": {
              border: 0,
              backgroundColor: color.primary.main,
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 300ms ease-in-out",
              "&:after": {
                content: "''",
                width: 8,
                height: 8,
                borderRadius: "50%",
                backgroundColor: color.white,
                display: "inline-block",
                transform: "scale(1)",
                transition: "all 1s ease",
              },
            },
          },
          "& ~ span": {
            fontWeight: 700,
            fontFamily: fontFamSecondary,
          },
        },
      },
      "& .MuiFormControlLabel-root": {
        "& + .MuiFormControlLabel-root": {
          marginTop: 16,
        },
      },
    },
    termContent: {
      "& p": {
        margin: 0,
        marginBottom: 12,
      },
      "& > ol, & > ul": {
        margin: 0,
        paddingLeft: 26,
        "& > li": {
          marginTop: 32,

          "&::marker": {
            fontWeight: 700,
          },
          "& > strong": {
            marginBottom: 12,
            display: "block",
          },
          "& ol, & ul": {
            paddingLeft: 16,
            "& li": {
              marginBottom: 8,
              "&:last-of-type": {
                marginBottom: 0,
              },
            },
          },
        },
      },
    },
    definitionSection: {
      "& li": {
        "&::marker": {
          fontWeight: 700,
        },
      },
    },
    offsetScrollTo: {
      position: "relative",
      top: -10,
      zIndex: 0,
    },
    listPopup: {
      "& li": {
        "& + li": {
          paddingTop: 16,
          marginTop: 16,
          borderTop: `1px solid ${color.grey.light}`,
        },
      },
    },
    dialogTerms: {
      "& h2": {
        "& svg": {
          display: "none",
        },
      },
    },
    productItem: {
      "& svg": {
        width: 40,
        height: 40,
      },
    },
    gainAccess: {
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center",
      backgroundSize: "cover",
    },
    title: {
      fontSize: 20,
      lineHeight: "30px",
      marginBottom: 20,
    },
    dialogAccount: {
      "& .MuiPaper-root.MuiDialog-paperScrollBody, & .MuiPaper-root.MuiDialog-paperScrollPaper": {
        minHeight: noFullscreen ? 0 : "100vh",
        margin: 0,
        maxWidth: "100%",
        width: "100%",
        borderTopLeftRadius: isLogout ? "24px" : 0,
        borderTopRightRadius: isLogout ? "24px" : 0,
      },
    },
    dialogTitle: {
      padding: 0,
      position: "relative",
      zIndex: 2,
      transition: "all 0.5s ease",
      "&.scroll": {
        animation: `$smoothScroll 500ms forwards`,
      },
      "&.scrollBottom": {
        animation: `$smoothScrollBottom 500ms forwards`,
      },
    },
    inputOtp: {
      position: "relative",
      "& > div": {
        position: "relative",
        "& + span": {
          position: "absolute",
          bottom: 6,
          left: "50%",
          transform: "translateX(-50%)",
        },
      },
    },
    insightCard: {
      "& > div": {
        maxHeight: 272,
        "& a": {
          "& > div": {
            "& + div": {
              "& > div": {
                "&:nth-of-type(2)": {
                  fontSize: 14,
                  lineHeight: "20px",
                },
              },
            },
            "&:first-of-type": {
              "& > div": {
                "& > span": {
                  "& > span": {
                    paddingBottom: "64% !important",
                  },
                },
              },
            },
          },
        },
      },
    },
    transitionFromBottom: {
      animation: `$smoothScrollBottom 500ms forwards`,
    },
  }))();
};

export default useStyles;
